import * as React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { AutocompleteTextField, ComboField, DateField, TextInputField } from '../Forms/fields';
import { createTaskAsync } from '../../ajax/requests';
import {
    ClientData,
    CreateTaskItemData,
    TaskItemData,
    TaskItemType,
    UserData,
} from '../../models';
import { ApplicationState } from '../../store';
import { getMyUser } from '../../store/session/selectors';
import { getSettings } from '../../store/settings/selectors';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getTaskTypeIcon, getTaskTypeTranslationKey, taskTypesForCreation } from '../../pages/Tasks/taskUtils';
import {
    createClientSearchConfig,
} from '../../shared/clientSearch';
import AppButton from '../AppButton';

const getDefaultTaskTitle = (
    taskType: TaskItemType | undefined,
    translate: (key: TK) => string,
): string => {
    if (taskType === undefined) {
        return '';
    }

    return translate(getTaskTypeTranslationKey(taskType));
};

interface TaskCreateState {
    type?: TaskItemType;
    clientId?: string;
    assignedUserId?: string;
    title?: string;
    notes?: string;
    branchId?: string;
    dueDate: string;
}

export interface TaskCreateModalProps {
    show: boolean;
    onClose: () => void;
    onCreated?: (task: TaskItemData) => Promise<void> | void;
    initialType?: TaskItemType;
    initialClientId?: string;
    initialAssignedUserId?: string;
    allowTypeSelection?: boolean;
    allowClientSelection?: boolean;
    allowAssignedUserSelection?: boolean;
    users?: UserData[];
    clients?: ClientData[];
}

const TaskCreateModal: React.FC<TaskCreateModalProps> = ({
    show,
    onClose,
    onCreated,
    initialType = TaskItemType.ReminderClientFollowUp,
    initialClientId,
    initialAssignedUserId,
    allowTypeSelection = true,
    allowClientSelection = true,
    allowAssignedUserSelection = true,
    users = [],
    clients = [],
}) => {
    const t = useTranslations();
    const myUser = useSelector(getMyUser);
    const currentUserId = myUser?.id;
    const branches = useSelector((state: ApplicationState) => getSettings(state).general?.branches ?? {});
    const myBranchIds = React.useMemo(() => myUser?.branchIds ?? [], [myUser?.branchIds]);
    const showBranchSelector = myBranchIds.length > 1;
    const branchOptions = React.useMemo(
        () => myBranchIds.map(id => ({ value: id, label: branches[id]?.displayName || id })),
        [myBranchIds, branches]);
    const defaultAssignedUserId = initialAssignedUserId ?? currentUserId;
    const [error, setError] = React.useState<string | null>(null);
    const [isSubmitting, setSubmitting] = React.useState(false);
    const [state, setState] = React.useState<TaskCreateState>({
        type: initialType,
        clientId: initialClientId,
        assignedUserId: defaultAssignedUserId,
        title: getDefaultTaskTitle(initialType, t),
        dueDate: '',
    });

    const resetState = React.useCallback(() => {
        setState({
            type: initialType,
            clientId: initialClientId,
            assignedUserId: defaultAssignedUserId,
            title: getDefaultTaskTitle(initialType, t),
            dueDate: '',
        });
        setError(null);
        setSubmitting(false);
    }, [defaultAssignedUserId, initialClientId, initialType, t]);

    React.useEffect(() => {
        if (!show) {
            return;
        }

        resetState();
    }, [show, resetState]);

    const taskTypeOptions = React.useMemo(() => [
        { value: '', label: t(TK.none) },
        ...taskTypesForCreation.map(type => ({
            value: String(type),
            label: t(getTaskTypeTranslationKey(type)),
            icon: React.createElement(getTaskTypeIcon(type), { fontSize: 'small' }),
        })),
    ], [t]);

    const clientSearchConfig = React.useMemo(() => createClientSearchConfig(clients), [clients]);

    const userOptions = React.useMemo(() => [
        { value: '', label: t(TK.none) },
        ...users.map(user => ({ value: user.id, label: user.name || user.username || user.id })),
    ], [users, t]);

    const closeModal = () => {
        if (isSubmitting) {
            return;
        }

        onClose();
    };

    const handleCreateTask = async () => {
        setError(null);

        if (showBranchSelector && !state.branchId) {
            setError(t(TK.taskBranchRequired));
            return;
        }

        const dueDate = state.dueDate || moment().format('YYYY-MM-DDTHH:mm:ss');

        const payload: CreateTaskItemData = {
            type: state.type,
            clientId: state.clientId || undefined,
            assignedUserId: state.assignedUserId || undefined,
            title: state.title?.trim() || undefined,
            notes: state.notes?.trim() || undefined,
            branchId: showBranchSelector ? state.branchId : undefined,
            dueDate,
        };

        setSubmitting(true);
        try {
            const createdTask = await createTaskAsync(payload);
            await onCreated?.(createdTask);
            onClose();
        } catch {
            setError(t(TK.somethingWentWrong));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={show} toggle={closeModal} size="lg">
            <ModalHeader toggle={closeModal}>{t(TK.createTask)}</ModalHeader>
            <ModalBody>
                <div className="d-flex flex-wrap" style={{ gap: 'var(--spacer-S)' }}>
                    {allowTypeSelection && (
                        <div style={{ minWidth: 220, flex: 1 }}>
                            <ComboField
                                id="create-task-type"
                                label={t(TK.type)}
                                value={state.type !== undefined ? String(state.type) : undefined}
                                options={taskTypeOptions}
                                onChange={value => setState(prev => {
                                    const nextType = value === undefined ? undefined : Number(value) as TaskItemType;
                                    const currentDefaultTitle = getDefaultTaskTitle(prev.type, t);
                                    const nextDefaultTitle = getDefaultTaskTitle(nextType, t);
                                    const shouldReplaceTitle = !prev.title?.trim().length || prev.title === currentDefaultTitle;

                                    return {
                                        ...prev,
                                        type: nextType,
                                        title: shouldReplaceTitle ? nextDefaultTitle : prev.title,
                                    };
                                })}
                                disabled={isSubmitting}
                            />
                        </div>
                    )}

                    {allowClientSelection && (
                        <div style={{ minWidth: 260, flex: 1 }}>
                            <AutocompleteTextField
                                id="create-task-client"
                                label={t(TK.client)}
                                value={clientSearchConfig.getOptionByClientId(state.clientId) ?? ''}
                                options={clientSearchConfig.options}
                                optionLabelResolver={clientSearchConfig.optionLabelResolver}
                                searchTextResolver={clientSearchConfig.searchTextResolver}
                                searchMatcher={clientSearchConfig.searchMatcher}
                                onValueChanged={option => {
                                    const client = clientSearchConfig.getClientByOption(option);
                                    setState(prev => ({ ...prev, clientId: client?.id || undefined }));
                                }}
                                disabled={isSubmitting}
                            />
                        </div>
                    )}

                    {allowAssignedUserSelection && (
                        <div style={{ minWidth: 220, flex: 1 }}>
                            <ComboField
                                id="create-task-user"
                                label={t(TK.responsible)}
                                value={state.assignedUserId}
                                options={userOptions}
                                onChange={value => setState(prev => ({ ...prev, assignedUserId: value || undefined }))}
                                disabled={isSubmitting}
                            />
                        </div>
                    )}

                    {showBranchSelector && (
                        <div style={{ minWidth: 220, flex: 1 }}>
                            <ComboField
                                id="create-task-branch"
                                label={t(TK.userBranches)}
                                helpText={t(TK.userBranchesHelp)}
                                value={state.branchId}
                                options={branchOptions}
                                onChange={value => setState(prev => ({ ...prev, branchId: value || undefined }))}
                                disabled={isSubmitting}
                            />
                        </div>
                    )}
                </div>

                <div className="d-flex" style={{ gap: 'var(--spacer-S)', marginTop: 'var(--spacer-S)', alignItems: 'flex-end' }}>
                    <div style={{ flex: 3, minWidth: 0 }}>
                        <TextInputField
                            id="create-task-title"
                            label={t(TK.title)}
                            value={state.title ?? ''}
                            onChange={value => setState(prev => ({ ...prev, title: value }))}
                            disabled={isSubmitting}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 160 }}>
                        <DateField
                            id="create-task-due-date"
                            label={t(TK.dueDate)}
                            type="datetime"
                            minutesStep={15}
                            value={state.dueDate}
                            onChange={value => setState(prev => ({ ...prev, dueDate: value }))}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>

                <div style={{ marginTop: 'var(--spacer-S)' }}>
                    <TextInputField
                        id="create-task-notes"
                        type="textarea"
                        label={t(TK.notes)}
                        value={state.notes ?? ''}
                        onChange={value => setState(prev => ({ ...prev, notes: value }))}
                        disabled={isSubmitting}
                        rows={4}
                    />
                </div>

                {error && (
                    <div style={{ marginTop: 'var(--spacer-S)', color: 'var(--danger)' }}>
                        {error}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <AppButton onClick={closeModal} disabled={isSubmitting}>{t(TK.cancel)}</AppButton>
                <AppButton color="success" onClick={handleCreateTask} disabled={isSubmitting}>
                    {t(TK.createTask)}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};

export default TaskCreateModal;
